import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import EditAdviserDetails from "../Tabs/AdviserDetails/EditAdviserDetails";
import {
  buildEditAdviserForm,
  saveEditAdviserForm,
  subscribeEditAdviserForm,
} from "../../../context/Adviser/Utils/AdviserDetailsCrud";
import { getEditAdviserAuditChanges } from "../AuditLogs/auditHelpers";
import { logAuditChanges } from "../AuditLogs/auditLogger";

export default function EditAdviserDetailsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const rawKey =
    params.adviserKey || params.id || params.email || params.adviserId || "";
  const adviserKey = rawKey ? decodeURIComponent(rawKey) : "";

  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(buildEditAdviserForm());
  const [originalForm, setOriginalForm] = useState(buildEditAdviserForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!adviserKey) return;

    const unsub = subscribeEditAdviserForm(adviserKey, ({ details, form }) => {
      setDetails(details);
      setForm(form);
      setOriginalForm(form);
      setLoading(false);
    });

    return unsub;
  }, [adviserKey]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError("");

      const adviserId =
        details?.adviserId ||
        adviserKey ||
        details?.adviserDoc?.email ||
        details?.email ||
        state?.header?.email ||
        state?.email;

      if (!adviserId) {
        throw new Error("Adviser id/email not found.");
      }

      const auditChanges = getEditAdviserAuditChanges({
        originalForm,
        updatedForm: form,
      });

      await saveEditAdviserForm(adviserId, form);

      if (auditChanges.length) {
        await logAuditChanges({
          entity: "adviser",
          entityId: adviserId,
          changes: auditChanges,
          extra: {
            page: "EditAdviserDetailsPage",
          },
        });
      }

      navigate(
        state?.returnTo ||
          `/advisers/${encodeURIComponent(adviserKey)}?tab=adviserDetails`,
      );
    } catch (err) {
      // console.error("Failed to save adviser details:", err);
      setSaveError(err.message || "Failed to save adviser details.");
    } finally {
      setSaving(false);
    }
  }, [details, adviserKey, form, originalForm, navigate, state]);

  return (
    <div className="main-content">
      <Helmet>
        <title>Edit Adviser Details</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="smProfilePage">
          {loading ? (
            <div className="smLoading Content-Small">Loading profile…</div>
          ) : (
            <>
              {saveError ? (
                <div
                  className="Content-Small"
                  style={{ color: "red", marginBottom: 12 }}
                >
                  {saveError}
                </div>
              ) : null}

              <EditAdviserDetails
                adviserKey={adviserKey}
                form={form}
                setForm={setForm}
                saving={saving}
                onSave={handleSave}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
